import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
    const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || null);

  useEffect(() => {
      const initAuth = async () => {
          const storedToken = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');
          const storedRole = localStorage.getItem('userRole');

          if (storedToken && storedUser) {
              setToken(storedToken);

              try {
                  setUser(JSON.parse(storedUser));
              } catch (error) {
                  console.error("Failed to parse stored user:", error);
                  localStorage.removeItem('user'); // optional cleanup
                  setUser(null);
              }

              setUserRole(storedRole || null);
          }

          setLoading(false);
      };

    initAuth();
  }, []);

  const login = async (credentials, role = 'user') => {
    try {
      let response;
      
      switch (role) {
        case 'organizer':
          response = await authService.loginOrganizer(credentials);
          break;
        case 'agent':
          response = await authService.loginAgent(credentials);
          break;
        case 'admin':
          response = await authService.loginAdmin(credentials);
          break;
        default:
          response = await authService.login(credentials);
      }

      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userRole', role);
      
      setToken(token);
      setUser(user);
      setUserRole(role);
      
      toast.success('Login successful!');
      return { success: true, user, role };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Express (guest) checkout — email + phone only, behaves like a normal login
  const guestStart = async (data) => {
    try {
      const response = await authService.guestStart(data);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userRole', 'user');

      setToken(token);
      setUser(user);
      setUserRole('user');

      toast.success('You can now continue your purchase!');
      return { success: true, user };
    } catch (error) {
      const data = error.response?.data;
      const message = data?.message || 'Could not start guest checkout';
      toast.error(message);
      return { success: false, accountExists: !!data?.account_exists, error: message };
    }
  };

  const register = async (data, isOrganizer = false) => {
    try {
      await (isOrganizer 
        ? authService.registerOrganizer(data)
        : authService.register(data));

      toast.success('Registration successful! Please login.');
      return { success: true };
    } catch (error) {
      console.log('Registration error:', error.response?.data);
      
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.values(errors).forEach(errorArray => {
          errorArray.forEach(msg => toast.error(msg));
        });
      } else {
        const message = error.response?.data?.message || 'Registration failed';
        toast.error(message);
      }
      
      return { success: false, error: error.response?.data };
    }
  };

    const logout = async () => {
        const role = userRole; // save before clearing

        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.clear();
            setToken(null);
            setUser(null);
            setUserRole(null);

            toast.info('Logged out successfully');

            // 🔥 FORCE correct redirect
            if (role === 'admin') {
                window.location.href = '/admin/login';
            } else if (role === 'organizer') {
                window.location.href = '/organizer/login';
            } else if (role === 'agent') {
                window.location.href = '/agent/login';
            } else {
                window.location.href = '/login';
            }
        }
    };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    userRole,
    loading,
    login,
    guestStart,
    register,
    logout,
    updateUser,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

